import React, { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import {
    LinearProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import './SkuBaseForecastTable.css';
import {
    isGreaterOrEqualToZero,
    isMoneyGreaterThanZero,
    isMoneyGreaterThanZeroThreeDecimals,
    isWholeNumber
} from '../Utilities/validation';
import axiosInstance from "../AxiosInstance";
import { useColumnWidths } from '../Utilities/columnWidthUtils';
import {getChangedWeeklyDistributionRates, isFieldPast} from "../Utilities/distributionRateUtils";
import { formatWeekHeader } from "../Utilities/weekNumberUtils";
import { formatCurrencyTwoToThreeDecimals } from '../Utilities/numberFormatter';
import ResetDialog from './shared/ResetDialog';
import OverrideIndicatorCell from './shared/OverrideIndicatorCell';
import { useResetDialog } from './shared/useResetDialog';
import SkuBaseForecastCreateDialog from "./SkuBaseForecastCreateDialog";
import SkuBaseForecastImportDialog from "./SkuBaseForecastImportDialog";
import ConfirmDialog from "./shared/ConfirmDialog";
import SkuBaseForecastToolbar from "./SkuBaseForecastToolbar";

const defaultColumnWidths = {
    skuDescription: 100
};

const SkuBaseForecastTable = ({
    forecastId,
    forecastPeriodId,
    customerId,
    customerName,
    isLocked,
    promotionProductsData,
    hasForecastWeekElapsed,
    allowPastWeeks = false,
    onRefreshPromotions,
    rows,
    onReload,
    onPromotionCellClick
}) => {
    const apiRef = useGridApiRef();
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedCells, setSelectedCells] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        unitCost: 0,
        unitLogs: 0,
        recommendedRetailPrice: 0,
        distribution: 0,
        rateOfSale: 0,
        productIds: [],
    });
    const [productErrorText, setProductErrorText] = useState('');
    const [unitCostErrorText, setUnitCostErrorText] = useState('');
    const [unitLogsErrorText, setUnitLogsErrorText] = useState('');
    const [recommendedRetailPriceErrorText, setRecommendedRetailPriceErrorText] = useState('');
    const [distributionErrorText, setDistributionErrorText] = useState('');
    const [rateOfSaleErrorText, setRateOfSaleErrorText] = useState('');
    const [editedRows, setEditedRows] = useState({});
    const [unsavedCellChanges, setUnsavedCellChanges] = useState(new Set());
    const [snackbar, setSnackbar] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [skuBaseForecastToDelete, setSkuBaseForecastToDelete] = useState(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const { columnWidths, handleColumnResize } = useColumnWidths('sbf', defaultColumnWidths);
    const [products, setProducts] = useState([]);
    const isCreatingSkuBaseForecastRef = useRef(false);

    const { resetDialog, handleResetDialogClose, handleResetConfirm, openResetDialog } = useResetDialog({
        rows,
        editedRows,
        setEditedRows,
        apiRef,
        setSnackbar,
        fieldType: 'actualDistributionRate',
        setUnsavedCellChanges
    });

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleClickCreate = () => {
        const existingProductIds = getExistingProductIds();
        
        setOpen(true);
        setFormData({
            unitCost: 0,
            unitLogs: 0,
            recommendedRetailPrice: 0,
            distribution: 0,
            rateOfSale: 0,
            productIds: []
        });
        setProductErrorText('');
        setUnitCostErrorText('');
        setUnitLogsErrorText('');
        setRecommendedRetailPriceErrorText('');
        setDistributionErrorText('');
        setRateOfSaleErrorText('');
        setProducts(existingProductIds);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpenDeleteDialog = (skuBaseForecastId) => {
        setSkuBaseForecastToDelete(skuBaseForecastId);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setSkuBaseForecastToDelete(null);
    };

    const handleOpenImportDialog = () => {
        setImportDialogOpen(true);
    };

    const handleCloseImportDialog = () => {
        setImportDialogOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!skuBaseForecastToDelete) return;

        try {
            setLoading(true);
            const response = await axiosInstance.delete(`/forecasts/${forecastId}/skubaseforecasts/${skuBaseForecastToDelete}?forecastPeriodId=${forecastPeriodId}`);

            if (response.status === 200) {
                onReload(rows.filter(row => row.id !== skuBaseForecastToDelete));
                setSnackbar({ children: "SKU Customer Forecast deleted successfully.", severity: "success" });

                if (onRefreshPromotions) {
                    onRefreshPromotions();
                }
            }
            setSelectedRowId(null);
            await onReload();
        } catch (error) {
            console.error(error.response);
            const errorMessage = typeof error.response?.data === 'string' ? error.response.data : 'Error deleting SKU customer forecast.';
            setSnackbar({ children: errorMessage, severity: 'error' });
        } finally {
            handleCloseDeleteDialog();
            setLoading(false);
        }
    };

    const handleCreateSkuBaseForecast = async (data) => {
        if (isCreatingSkuBaseForecastRef.current) {
            return;
        }

        const isValidUnitCost = isMoneyGreaterThanZeroThreeDecimals(data.unitCost);
        const isValidUnitLogs = isMoneyGreaterThanZeroThreeDecimals(data.unitLogs);
        const isValidRecommendedRetailPrice = isMoneyGreaterThanZero(data.recommendedRetailPrice);
        const isValidDistribution = isWholeNumber(data.distribution);
        const isValidRateOfSale = isGreaterOrEqualToZero(data.rateOfSale);
        const isValidProducts = data.productIds.length > 0;

        if (!isValidUnitCost ||
            !isValidUnitLogs ||
            !isValidRecommendedRetailPrice ||
            !isValidDistribution ||
            !isValidRateOfSale ||
            !isValidProducts) {
            let threeDpErrorMessage = 'Please enter a valid number with up to three decimal places and greater than 0';
            setUnitCostErrorText(isValidUnitCost ? '' : threeDpErrorMessage);
            setUnitLogsErrorText(isValidUnitLogs ? '' : threeDpErrorMessage);
            setRecommendedRetailPriceErrorText(isValidRecommendedRetailPrice ? '' : 'Please enter a valid number with up to two decimal places and greater than 0');
            setDistributionErrorText(isValidDistribution ? '' : 'Please enter a valid number');
            setRateOfSaleErrorText(isValidRateOfSale ? '' : 'Please enter a valid number');
            setProductErrorText(isValidProducts ? '' : 'Please select at least one SKU');
            return;
        }

        setUnitCostErrorText('');
        setUnitLogsErrorText('');
        setRecommendedRetailPriceErrorText('');
        setDistributionErrorText('');
        setRateOfSaleErrorText('');
        setProductErrorText('');

        try {
            isCreatingSkuBaseForecastRef.current = true;
            setLoading(true);
            data.customerId = customerId;
            data.forecastPeriodId = forecastPeriodId;

            const requests = data.productIds.map(productId => {
                const forecastData = { ...data, productId };
                return axiosInstance.post(`/forecasts/${forecastId}/skubaseforecasts`, forecastData);
            });

            const results = await Promise.allSettled(requests);

            const successful = [];
            const failed = [];

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    successful.push(data.productIds[index]);
                } else {
                    failed.push(data.productIds[index]);
                }
            });

            if (successful.length > 0) {
                handleClose();
                await onReload();
            }

            if (failed.length > 0) {
                const failedProductNames = failed.map(id => {
                    const product = products.find(p => p.productId === id);
                    return product ? product.skuDescription : `Product ID: ${id}`;
                }).join(', ');

                setSnackbar({
                    children: `Failed to add the following SKUs: ${failedProductNames}`,
                    severity: 'error',
                });
            } else if (successful.length > 0) {
                setSnackbar({
                    children: 'SKU Customer Forecasts created successfully.',
                    severity: 'success',
                });
            } else {
                setSnackbar({
                    children: 'Error creating SKU Customer Forecasts.',
                    severity: 'error',
                });
            }
        } catch (error) {
            console.error('Error creating SkuBaseForecast:', error);
            let errorMessage = 'Error creating SKU Customer Forecasts.';
            if (error.response && error.response.status === 500) {
                errorMessage = error.response.data || 'Server error while creating SKU Customer Forecasts.';
            }
            setSnackbar({ children: errorMessage, severity: 'error' });
        } finally {
            setLoading(false);
            isCreatingSkuBaseForecastRef.current = false;
        }
    };

    const isValidWeeklyDistributionRates = (weeklyDistributionRates) => {
        return weeklyDistributionRates.every(rateObj => {
            const intValue = parseInt(rateObj.actualDistributionRate, 10);
            return !isNaN(intValue) && intValue >= 0 && intValue === parseFloat(rateObj.actualDistributionRate);
        });
    };

    const transformEditedRowToOverrideRequest = (editedRow, originalRow, forecastPeriodId) => {
        const distributionRateOverrides = getChangedWeeklyDistributionRates(
            editedRow.weeklyDistributionRates,
            originalRow.weeklyDistributionRates
        ).map(change => {
            return {
                SkuBaseForecastId: editedRow.id,
                WeekStart: change.week,
                DistributionRate: change.actualDistributionRate,
                ForecastPeriodId: forecastPeriodId
            };
        });

        let skuBaseForecastOverride = null;
        if (editedRow.unitLogs !== originalRow.unitLogs ||
            editedRow.recommendedRetailPrice !== originalRow.recommendedRetailPrice) {
            skuBaseForecastOverride = {
                SkuBaseForecastId: editedRow.id,
                UnitLogs: parseFloat(editedRow.unitLogs || 0),
                RecommendedRetailPrice: parseFloat(editedRow.recommendedRetailPrice || 0),
                ForecastPeriodId: forecastPeriodId
            };
        }

        return {
            DistributionRateOverrides: distributionRateOverrides,
            SkuBaseForecastOverride: skuBaseForecastOverride
        };
    };

    const handleSaveClick = async () => {
        try {
            setLoading(true);
            const rowsArray = Object.values(editedRows);

            const transformedRows = rowsArray.map(editedRow => {
                const originalRow = rows.find(row => row.id === editedRow.id);
                return transformEditedRowToOverrideRequest(editedRow, originalRow, forecastPeriodId);
            });

            const payload = {
                DistributionRateOverrides: [].concat(...transformedRows.map(row => row.DistributionRateOverrides)),
                SkuBaseForecastOverrides: transformedRows.map(row => row.SkuBaseForecastOverride).filter(override => override !== null),
                forecastPeriodId: forecastPeriodId
            };

            await axiosInstance.post(`/forecasts/${forecastId}/skubaseforecasts/overrides/bulk`, payload);

            setSnackbar({ children: 'Changes successfully updated.', severity: 'success' });
            setEditedRows({});
            setUnsavedCellChanges(new Set());
            await onReload();
        } catch (error) {
            console.error(error.response);
            const errorMessage = typeof error.response?.data === 'string' ? error.response.data : 'Error saving changes.';
            setSnackbar({ children: errorMessage, severity: 'error' });
        } finally {
            setLoading(false);
        }
    }

    const CustomToolbar = () => {
        const hasEdits = Object.keys(editedRows).length > 0;
        return (
            <SkuBaseForecastToolbar
                isLocked={isLocked}
                hasEdits={hasEdits}
                selectedRowId={selectedRowId}
                onImport={handleOpenImportDialog}
                onCreate={handleClickCreate}
                onSave={handleSaveClick}
                onDelete={handleOpenDeleteDialog}
            />
        );
    };

    const handleProductSelected = (productIds) => {
        setFormData((prevState) => ({
            ...prevState,
            productIds: productIds,
        }));
    };

    const handleTextFieldChange = (event) => {
        const { name, value } = event.target;

        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleCellSelectionModelChange = useCallback(() => {
        const selected = apiRef.current.getSelectedCellsAsArray();
        setSelectedCells(selected);
    }, [apiRef]);


    function moveTopLevelDateKeysToNested(newRow) {
        const weeklyRatesMap = newRow.weeklyDistributionRates.reduce((acc, current) => {
            acc[current.week] = current;
            return acc;
        }, {});

        const datePattern = /^\d{2}-\d{2}-\d{4}$/;

        Object.keys(newRow).forEach(key => {
            if (datePattern.test(key) && weeklyRatesMap[key]) {
                weeklyRatesMap[key].actualDistributionRate = newRow[key];
                delete newRow[key];
            }
        });

        newRow.weeklyDistributionRates = Object.values(weeklyRatesMap);

        return newRow;
    }

    const processRowUpdates = (newRow, oldRow) => {
        let validationErrors = [];

        const copiedNewRow = JSON.parse(JSON.stringify(newRow));
        moveTopLevelDateKeysToNested(copiedNewRow);
        const changedRates = getChangedWeeklyDistributionRates(copiedNewRow.weeklyDistributionRates, oldRow.weeklyDistributionRates);
        const weeksToMark = new Set(changedRates.map(change => change.week));

        if (!isValidWeeklyDistributionRates(changedRates)) {
            validationErrors.push("Please enter a valid value for weekly distribution rate.");
            copiedNewRow.weeklyDistributionRates = oldRow.weeklyDistributionRates;
        }

        if (!isMoneyGreaterThanZeroThreeDecimals(newRow.unitLogs)) {
            validationErrors.push("Please enter a valid value for unit logs.");
            copiedNewRow.unitLogs = oldRow.unitLogs;
        }

        if (!isMoneyGreaterThanZero(newRow.recommendedRetailPrice)) {
            validationErrors.push("Please enter a valid value for RRP.");
            copiedNewRow.recommendedRetailPrice = oldRow.recommendedRetailPrice;
        }

        if (changedRates.length > 0)
        {
            const newValue = changedRates[0].actualDistributionRate;
            selectedCells.forEach((cell) => {
                const weekIndex = copiedNewRow.weeklyDistributionRates?.findIndex(
                    (w) => w.week === cell.field
                );

                if (weekIndex !== -1 && weekIndex !== undefined) {
                    if (isCellEditable({ row: copiedNewRow, field: cell.field })) {
                        copiedNewRow.weeklyDistributionRates[weekIndex].actualDistributionRate = newValue;
                        weeksToMark.add(cell.field);
                    }
                }
            });
        }
        
        if (weeksToMark.size > 0) {
            setUnsavedCellChanges(prev => {
                const newSet = new Set(prev);
                weeksToMark.forEach(week => {
                    newSet.add(`${copiedNewRow.id}-${week}`);
                });
                return newSet;
            });
        }
        
        setEditedRows(prevState => ({
            ...prevState,
            [copiedNewRow.id]: copiedNewRow
        }));

        if (validationErrors.length > 0) {
            const combinedErrorMessage = validationErrors.join("\n");
            setSnackbar({
                children: combinedErrorMessage,
                severity: 'error'
            });
        }

        return copiedNewRow;
    }

    const handleSelectionModelChange = (newSelectionModel) => {
        const newSelectedRowId = newSelectionModel[0] || null;
        setSelectedRowId(newSelectedRowId);
    };

    const handleProcessRowUpdateError = useCallback((error) => {
        setSnackbar({ children: error.message, severity: 'error' });
    }, []);

    const getExistingProductIds = () => {
        return rows.map(row => row.productId);
    };

    const isCellEditable = (params) => {
        const { row, field } = params;
        const weekData = row.weeklyDistributionRates.find(w => w.week === field);

        if (weekData?.isDuringPromotion) return false;

        return !(isFieldPast(field, allowPastWeeks) || isLocked);
    };

    const { columns, columnGroupingModel } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const monthMap = new Map(); // Map for grouping weeks by month
        const weeklyDistributionRateColumns = rows.length && rows[0]?.weeklyDistributionRates
            ? rows[0].weeklyDistributionRates.map((wdr, index) => {
                const [day, month, year] = wdr.week.split('-').map(Number);
                const columnDate = new Date(year, month - 1, day);
                const customMonth = wdr.forecastWeek.month;
                const monthName = new Date(2000, customMonth - 1, 1)
                    .toLocaleString('default', { month: 'short' })
                    .toUpperCase();
                const fiscalPeriod = wdr.forecastWeek.periodIndex;
                const useRetailPeriodLabels = rows[0]?.fiscalCalendarRuleSet === 1;
                const monthHeader = useRetailPeriodLabels
                    ? `P${fiscalPeriod} - ${monthName}`
                    : monthName;

                const column = {
                    field: wdr.week,
                    headerName: formatWeekHeader(wdr.week, index),
                    type: 'number',
                    width: 100,
                    editable: true,
                    valueGetter: (value, row) => row.weeklyDistributionRates.find(w => w.week === wdr.week)?.actualDistributionRate,
                    cellClassName: (params) => {
                        const classes = [];

                        const weekData = params.row.weeklyDistributionRates.find(w => w.week === params.field);
                        const isPastOrLocked = isFieldPast(params.field, allowPastWeeks) || isLocked;

                        const isHighlighted =
                            promotionProductsData &&
                            promotionProductsData.some(pp =>
                                pp.skuBaseForecastId === params.id &&
                                pp.weeklyDistributionRates?.includes(params.field)
                            );

                        if (isHighlighted) {
                            classes.push('highlightedCell');
                        }

                        if (weekData?.isDuringPromotion || isPastOrLocked) {
                            classes.push('nonEditableCell');
                        }

                        return classes.join(' ');
                    },
                    renderCell: (params) => {
                        const weekData = params.row.weeklyDistributionRates.find(w => w.week === params.field);
                        const hasOverride = weekData?.hasDistributionRateOverride || false;
                        const value = weekData?.actualDistributionRate || 0;
                        const isPastOrLocked = isFieldPast(params.field, allowPastWeeks) || isLocked;
                        const isDuringPromotion = weekData?.isDuringPromotion || false;
                        
                        const distribution = weekData?.distribution || 0;
                        const rateOfSale = weekData?.rateOfSale || 0;
                        const baseValue = Math.ceil(distribution * rateOfSale);
                        const hasPromoOverride = weekData?.hasPromoOverride || false;
                        const basePromoDistributionRate = weekData?.basePromoDistributionRate;
                        const originalBaseDistributionRate = weekData?.originalBaseDistributionRate || 0;
                        const upliftPercentRaw = weekData?.upliftPercent;
                        const percentUplift = upliftPercentRaw !== null && upliftPercentRaw !== undefined
                            ? Number(upliftPercentRaw)
                            : 0;
                        const promoDistributionRate = weekData?.promoDistributionRate;
                        const promoBaseValue = basePromoDistributionRate !== null && basePromoDistributionRate !== undefined
                            ? Number(basePromoDistributionRate)
                            : Number(originalBaseDistributionRate);
                        const effectivePromoValue = promoDistributionRate !== null && promoDistributionRate !== undefined
                            ? Number(promoDistributionRate)
                            : promoBaseValue;
                        const promoOverrideDetails = hasPromoOverride ? {
                            baseValue: promoBaseValue,
                            percentUplift,
                            effectiveValue: effectivePromoValue,
                            isRateOverridden: weekData?.hasPromoRateOverride || false
                        } : undefined;
                        
                        const cellKey = `${params.id}-${params.field}`;
                        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
                        
                        return (
                            <OverrideIndicatorCell
                                value={value}
                                hasOverride={hasOverride}
                                isPastOrLocked={isPastOrLocked}
                                isDuringPromotion={isDuringPromotion}
                                hasUnsavedChanges={hasUnsavedChanges}
                                promoOverrideDetails={promoOverrideDetails}
                                onDotClick={() => openResetDialog(params.id, params.field, value, baseValue)}
                            />
                        );
                    }
                };

                if (!monthMap.has(monthHeader)) {
                    monthMap.set(monthHeader, []);
                }
                monthMap.get(monthHeader).push({ field: wdr.week });

                return column;
            })
            : [];

        const fyTotalColumn = {
            field: 'fyTotal',
            headerName: 'FY Total',
            type: 'number',
            width: 120,
            valueGetter: (value, row) => {
                return row.weeklyDistributionRates.reduce((sum, current) => sum + (current.actualDistributionRate || 0), 0);
            },
            cellClassName: 'nonEditableCell'
        };

        const columns = [
            { field: 'id', headerName: 'ID', width: 90 },
            {
                field: 'skuDescription',
                headerName: 'SKU',
                width: columnWidths.skuDescription,
                editable: false,
            },
            {
                field: 'unitLogs',
                headerName: 'Logs',
                type: 'number',
                width: 100,
                editable: true,
                valueFormatter: (value) => formatCurrencyTwoToThreeDecimals(value),
            },
            {
                field: 'recommendedRetailPrice',
                headerName: 'RRP',
                type: 'number',
                width: 100,
                editable: true,
                valueFormatter: (value) => '£' + parseFloat(value).toFixed(2),
            },
            ...weeklyDistributionRateColumns,
            fyTotalColumn
        ];

        const columnGroupingModel = Array.from(monthMap, ([groupId, children]) => ({
            groupId,
            headerName: groupId,
            children,
        }));

        return { columns, columnGroupingModel };
    }, [rows, isLocked, columnWidths, promotionProductsData, unsavedCellChanges]);


    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPremium
                apiRef={apiRef}
                editMode="row"
                rows={rows}
                columns={columns}
                loading={loading}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{ columnGrouping: true }}
                isCellEditable={isCellEditable}
                onColumnResize={handleColumnResize}
                onCellDoubleClick={(params) => {
                    // Check if it's a weekly distribution rate column
                    const isWeeklyDistributionColumn = /^\d{2}-\d{2}-\d{4}$/.test(params.field);
                    if (isWeeklyDistributionColumn) {
                        setTimeout(() => {
                            const input = document.querySelector(`[data-id="${params.id}"] [data-field="${params.field}"] input`);
                            if (input) {
                                input.select();
                            }
                        }, 100);
                    }
                }}
                onCellKeyDown={(params, event) => {
                    if (event.key === 'Tab') {
                        // For tab navigation, we need to wait for the focus to move to the new cell
                        setTimeout(() => {
                            const focusedInput = document.activeElement;
                            if (focusedInput && focusedInput.tagName === 'INPUT' && focusedInput.type === 'number') {
                                focusedInput.select();
                            }
                        }, 50);
                    }
                }}
                onCellClick={(params) => {
                    setSelectedRowId(params.id);

                    const isWeeklyDistributionColumn = /^\d{2}-\d{2}-\d{4}$/.test(params.field);
                    if (isWeeklyDistributionColumn) {
                        onPromotionCellClick?.({
                            skuBaseForecastId: params.id,
                            week: params.field
                        });
                    }
                }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    pinnedColumns: { left: ['skuDescription'] }
                }}
                pageSize={10}
                slots={{
                    toolbar: CustomToolbar,
                    loadingOverlay: LinearProgress,
                }}
                cellSelection
                onCellSelectionModelChange={handleCellSelectionModelChange}
                disableClipboardPaste
                processRowUpdate={processRowUpdates}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                selectionModel={selectedRowId ? [selectedRowId] : []}
                onSelectionModelChange={handleSelectionModelChange}
                onRowClick={(params) => setSelectedRowId(params.id)}
            />
            <SkuBaseForecastCreateDialog
                open={open}
                loading={loading}
                formData={formData}
                productErrorText={productErrorText}
                unitCostErrorText={unitCostErrorText}
                unitLogsErrorText={unitLogsErrorText}
                recommendedRetailPriceErrorText={recommendedRetailPriceErrorText}
                distributionErrorText={distributionErrorText}
                rateOfSaleErrorText={rateOfSaleErrorText}
                existingProductIds={getExistingProductIds()}
                onClose={handleClose}
                onCreate={handleCreateSkuBaseForecast}
                onProductsSelected={handleProductSelected}
                onFormFieldChange={handleTextFieldChange}
                onProductsLoaded={setProducts}
            />
            <SkuBaseForecastImportDialog
                open={importDialogOpen}
                forecastId={forecastId}
                forecastPeriodId={forecastPeriodId}
                customerId={customerId}
                customerName={customerName}
                onClose={handleCloseImportDialog}
                onImported={async (result) => {
                    await onReload();

                    if (onRefreshPromotions) {
                        await onRefreshPromotions();
                    }

                    setSnackbar({
                        children: result.message || "SKU Customer Forecasts imported successfully.",
                        severity: "success"
                    });
                }}
            />
            <ConfirmDialog
                open={deleteDialogOpen}
                title="Delete SKU Customer Forecast"
                message="Are you sure you want to delete this SKU customer forecast? This action cannot be undone."
                confirmLabel="OK"
                cancelLabel="Cancel"
                onClose={handleCloseDeleteDialog}
                onConfirm={handleConfirmDelete}
            />
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={12000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
            <ResetDialog
                open={resetDialog.open}
                currentValue={resetDialog.currentValue}
                baseValue={resetDialog.baseValue}
                onClose={handleResetDialogClose}
                onConfirm={handleResetConfirm}
            />
        </Box>
    );
};

export default SkuBaseForecastTable;
